import type { Dispatch, SetStateAction } from 'react'
import { useCallback, useState } from 'react'
import type { StartSessionFormState } from '../types'
import { INITIAL_START_SESSION_FORM } from '../types'
import { useStartSession } from './useStartSession'

type SharedStep = 'initial_message' | 'permissions' | 'review'

interface UseStartSessionFormOptions {
  readonly setStep: (step: SharedStep) => void
}

interface UseStartSessionFormReturn {
  readonly form: StartSessionFormState
  readonly setForm: Dispatch<SetStateAction<StartSessionFormState>>
  readonly handleSessionNameNext: (sessionName: string) => void
  readonly handleInitialMessageNext: (initialMessage: string) => void
  readonly handlePermissionsNext: (dangerouslySkipPermissions: boolean) => void
  readonly resetForm: () => void
  readonly startSession: ReturnType<typeof useStartSession>['startSession']
  readonly isSubmitting: boolean
  readonly error: string | null
}

export function useStartSessionForm({
  setStep,
}: UseStartSessionFormOptions): UseStartSessionFormReturn {
  const [form, setForm] = useState<StartSessionFormState>(INITIAL_START_SESSION_FORM)
  const { startSession, isSubmitting, error } = useStartSession()

  const resetForm = useCallback(() => {
    setForm(INITIAL_START_SESSION_FORM)
  }, [])

  const handleSessionNameNext = useCallback(
    (sessionName: string) => {
      setForm((prev) => ({ ...prev, sessionName }))
      setStep('initial_message')
    },
    [setStep],
  )

  const handleInitialMessageNext = useCallback(
    (initialMessage: string) => {
      setForm((prev) => ({ ...prev, initialMessage }))
      setStep('permissions')
    },
    [setStep],
  )

  const handlePermissionsNext = useCallback(
    (dangerouslySkipPermissions: boolean) => {
      setForm((prev) => ({ ...prev, dangerouslySkipPermissions }))
      setStep('review')
    },
    [setStep],
  )

  return {
    form,
    setForm,
    handleSessionNameNext,
    handleInitialMessageNext,
    handlePermissionsNext,
    resetForm,
    startSession,
    isSubmitting,
    error,
  }
}
